import { useState } from "react";
import type {
  AccountPermissionNode,
  OperatorAuthority
} from "@dealer-center/ddc-pages-configuration-interface";
import { AccountSubtree } from "./AccountSubtree";

export interface AccountNodeProps {
  node: AccountPermissionNode;
  /** 1-based, matching `aria-level`. Defaults to a root. */
  depth?: number;
  /**
   * The **operator's** authority. **Absent means read-only** — the same default, for the same
   * reasons, as `FeatureAccessTabProps.authority`: My Info passes nothing and is unaffected, and
   * enabling is opt-in and requires the operator's own record, so no row can become editable from
   * data about the subject (FR-054).
   */
  authority?: OperatorAuthority;
}

// `node` and every reachable descendant, unchecked; unreachable ones are skipped but still descended.
export const uncheckSubtree = (
  node: AccountPermissionNode,
  reachableAccountIds: ReadonlySet<string>
): Record<string, boolean> =>
  (node.children ?? []).reduce<Record<string, boolean>>(
    (unchecked, child) => ({
      ...unchecked,
      ...uncheckSubtree(child, reachableAccountIds)
    }),
    reachableAccountIds.has(node.accountId) ? { [node.accountId]: false } : {}
  );

// The nodes from `node` down to `accountId`, or `[]` when it is not in this subtree.
const pathTo = (
  node: AccountPermissionNode,
  accountId: string
): AccountPermissionNode[] => {
  if (node.accountId === accountId) {
    return [node];
  }
  const below = (node.children ?? [])
    .map(child => pathTo(child, accountId))
    .find(path => path.length > 0);
  return below ? [node, ...below] : [];
};

// `accountId` and every reachable ancestor up to `root`, checked; unreachable ancestors are skipped.
export const checkWithAncestors = (
  root: AccountPermissionNode,
  accountId: string,
  reachableAccountIds: ReadonlySet<string>
): Record<string, boolean> =>
  pathTo(root, accountId)
    .filter(ancestor => reachableAccountIds.has(ancestor.accountId))
    .reduce<Record<string, boolean>>(
      (checked, ancestor) => ({ ...checked, [ancestor.accountId]: true }),
      {}
    );

// A root of the hierarchy; owns its subtree's unsaved toggles and renders it via `AccountSubtree`.
export const AccountNode = ({
  node,
  depth = 1,
  authority
}: AccountNodeProps) => {
  const [overrides, setOverrides] = useState<Readonly<Record<string, boolean>>>(
    {}
  );
  // Checking cascades up to reachable ancestors; unchecking cascades down to reachable descendants.
  const handleToggle = (toggled: AccountPermissionNode, isChecked: boolean) => {
    const reachableAccountIds =
      authority?.reachableAccountIds ?? new Set<string>();
    const changes = isChecked
      ? checkWithAncestors(node, toggled.accountId, reachableAccountIds)
      : uncheckSubtree(toggled, reachableAccountIds);
    setOverrides(current => ({ ...current, ...changes }));
  };

  return (
    <AccountSubtree
      authority={authority}
      depth={depth}
      node={node}
      overrides={overrides}
      onToggle={handleToggle}
    />
  );
};
